import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ModalTemplate from "../../Common/ModalTemplate";
import AppButton from "../../Common/AppButton";
import useRfiStagesActivityLogTable from "../../../hooks/useRfiStagesActivityLogTable";
import { getActionTypeConfig } from "../../../constants/rfiActivityLogConstants";
import { formatUnderscoreText } from "../../../utils/activityLogUtils";
import {
  PerformerCell,
  StageCell,
  StatusChangeCell,
  TimestampCell,
  MessageCell,
} from "./TableCellRenderers";
import ExpandableRowDetails from "./ExpandableRowDetails";

/**
 * Styles for the activity log table
 * Uses theme values for consistency
 */
const getStyles = (theme) => ({
  tableContainer: {
    position: "relative",
    minHeight: 500,
    maxHeight: 700,
    overflowY: "auto",
    overflowX: {
      xs: "auto",
      md: "hidden",
    },
    "@media (max-width: 820px)": {
      overflowX: "auto",
    },
  },
  tableHeaderCell: {
    color: theme.palette.text.light,
    fontWeight: 800,
    backgroundColor: theme.palette.background.dark,
    position: "sticky",
    top: 0,
    zIndex: 3,
    whiteSpace: "nowrap",
    px: 1,
    py: 1,
    fontSize: "0.813rem",
  },
  tableCell: {
    px: 1,
    py: 1,
    verticalAlign: "top",
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "rgba(255, 255, 255, 0.8)",
    zIndex: 10,
  },
  textField: {
    bgcolor: theme.palette.background.paper,
    borderRadius: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  datePicker: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    "& .MuiInputLabel-root": {
      "&.Mui-focused, &.MuiFormLabel-filled": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
      "&:not(.Mui-focused):not(.MuiFormLabel-filled)": {
        transform: "translate(14px, 8px)",
      },
    },
    "& .MuiPickersSectionList-root": {
      backgroundColor: theme.palette.background.default,
      padding: "8px",
    },
    "& .MuiPickersInputBase-sectionsContainer": {
      gap: "8px",
      minHeight: "40px",
    },
  },
});

/**
 * RFI Stages Activity Log Modal Component
 * Displays filterable, paginated activity logs for an RFI
 */
const RfiStagesActivityLog = ({ open, onClose, rfiNo }) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  // Use custom hook for all table logic
  const {
    filterActionTypes,
    setFilterActionTypes,
    filterStages,
    setFilterStages,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    filterContributors,
    setFilterContributors,
    logs,
    isLoading,
    page,
    totalPages,
    totalLogs,
    actionTypes,
    stages,
    contributors,
    rfiCreatedAt,
    handleToggleExpand,
    handleClearAllFilters,
    handlePageChange,
    hasExpandableDetails,
    isRowExpanded,
  } = useRfiStagesActivityLogTable({ rfiNo, open });

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={`Activity Log - RFI #${rfiNo}`}
      maxWidth={false}
      showActions={false}
      customPaperProps={{
        sx: {
          width: "99vw",
          maxWidth: "3200px",
          height: "92vh",
          maxHeight: "1400px",
        },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total Activities: {totalLogs} | Showing: {logs.length} (Page {page}{" "}
            of {totalPages})
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200, ...styles.textField }}>
            <InputLabel id="filter-action-type-label">
              Filter by Action
            </InputLabel>
            <Select
              labelId="filter-action-type-label"
              multiple
              value={filterActionTypes}
              onChange={(e) => setFilterActionTypes(e.target.value)}
              label="Filter by Action"
              renderValue={(selected) =>
                selected.length === 0
                  ? "All Actions"
                  : `${selected.length} selected`
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 500,
                  },
                },
              }}
            >
              {actionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={filterActionTypes.indexOf(type) > -1} />
                  <ListItemText primary={formatUnderscoreText(type)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200, ...styles.textField }}>
            <InputLabel id="filter-stage-label">Filter by Stage</InputLabel>
            <Select
              labelId="filter-stage-label"
              multiple
              value={filterStages}
              onChange={(e) => setFilterStages(e.target.value)}
              label="Filter by Stage"
              renderValue={(selected) =>
                selected.length === 0
                  ? "All Stages"
                  : `${selected.length} selected`
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 500,
                  },
                },
              }}
            >
              {stages.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  <Checkbox checked={filterStages.indexOf(stage) > -1} />
                  <ListItemText primary={formatUnderscoreText(stage)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200, ...styles.textField }}>
            <InputLabel id="filter-contributor-label">
              Filter by Contributor
            </InputLabel>
            <Select
              labelId="filter-contributor-label"
              multiple
              value={filterContributors}
              onChange={(e) => setFilterContributors(e.target.value)}
              label="Filter by Contributor"
              renderValue={(selected) =>
                selected.length === 0
                  ? "All Contributors"
                  : `${selected.length} selected`
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 500,
                  },
                },
              }}
            >
              {contributors.map((contributor) => (
                <MenuItem key={contributor.user_id} value={contributor.user_id}>
                  <Checkbox
                    checked={
                      filterContributors.indexOf(contributor.user_id) > -1
                    }
                  />
                  <ListItemText
                    primary={`${contributor.full_name} (${formatUnderscoreText(
                      contributor.role
                    )})`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={enGB}
          >
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue);
                // Clear end date if start date is cleared
                if (!newValue) {
                  setEndDate(null);
                }
              }}
              shouldDisableDate={(date) => {
                if (!rfiCreatedAt) return false;
                return date < rfiCreatedAt;
              }}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 200, ...styles.textField },
                },
                field: { clearable: true },
              }}
              sx={styles.datePicker}
            />

            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              disabled={!startDate}
              shouldDisableDate={(date) => {
                if (rfiCreatedAt && date < rfiCreatedAt) return true;
                if (startDate && date < startDate) return true;
                return false;
              }}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 200, ...styles.textField },
                },
                field: { clearable: true },
              }}
              sx={styles.datePicker}
            />
          </LocalizationProvider>

          <AppButton
            onClick={handleClearAllFilters}
            label="Reset"
            buttonType="greyed"
            size="small"
            sx={{ minWidth: 100 }}
          />
        </Box>
      </Box>

      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table
          stickyHeader
          size="small"
          aria-label="rfi stages activity log table"
          sx={{
            minWidth: {
              xs: 1200,
              md: "100%",
            },
            "@media (max-width: 820px)": {
              minWidth: 1200,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ ...styles.tableHeaderCell, width: "50px" }}
                align="center"
              ></TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell, width: "160px" }}>
                Action Type
              </TableCell>
              <TableCell
                sx={{
                  ...styles.tableHeaderCell,
                  width: "auto",
                  minWidth: "200px",
                }}
              >
                Message
              </TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell, width: "120px" }}>
                Stage
              </TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell, width: "150px" }}>
                Status Change
              </TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell, width: "140px" }}>
                Performed By
              </TableCell>
              <TableCell sx={{ ...styles.tableHeaderCell, width: "150px" }}>
                Timestamp
              </TableCell>
              <TableCell
                sx={{
                  ...styles.tableHeaderCell,
                  width: "50px",
                }}
                align="center"
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? null : logs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{
                    height: 400,
                    verticalAlign: "middle",
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    No activity logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, idx) => {
                const config = getActionTypeConfig(log.action_type);
                const hasDetails = hasExpandableDetails(log);
                const isExpanded = isRowExpanded(log.log_id);

                return (
                  <React.Fragment key={log.log_id}>
                    <TableRow
                      hover
                      sx={{
                        bgcolor:
                          idx % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                      }}
                    >
                      <TableCell sx={styles.tableCell} align="center">
                        {hasDetails && (
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpand(log.log_id)}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Chip
                          icon={config.icon}
                          label={config.label}
                          size="small"
                          color={config.color}
                          sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <MessageCell log={log} />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <StageCell log={log} />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <StatusChangeCell log={log} />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <PerformerCell log={log} />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <TimestampCell log={log} />
                      </TableCell>
                      <TableCell
                        sx={styles.tableCell}
                        align="center"
                      ></TableCell>
                    </TableRow>
                    {hasDetails && (
                      <ExpandableRowDetails log={log} expanded={isExpanded} />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {isLoading && (
          <Box sx={styles.loadingOverlay}>
            <CircularProgress size={40} />
          </Box>
        )}
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </ModalTemplate>
  );
};

export default RfiStagesActivityLog;
